import { toUserMessage } from '../../lib/errors.js';
import { confirmDialog } from '../../lib/ui/dialogs.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { addCircleOutline, alertCircleOutline, chevronForward, copyOutline, documentTextOutline, layersOutline, removeCircleOutline, snowOutline, star, starOutline, trashOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { focusInput, fieldClass } from '../../components/forms.js';
import { SearchPickerModal, PickerTriggerField } from '../../components/SearchPickerModal.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateFood } from '../../lib/auth/capabilities.js';
import { useCollettivaEnabled, useEnqueueMaster, useMasters, useRecipeQuantitiesEnabled } from '../../lib/data/queries.js';
import {
  CCP_TYPE_LABELS,
  type CcpType,
  collettivaDietLabel,
  VARIANTABLE_DIET_CODES,
  ingredientsFingerprint,
} from '@trackeat/shared';
import { openLocalDb } from '../../lib/db/local.js';
import { FoodEditorModal, type FoodEditorValue } from '../../components/editors/FoodEditorModal.js';
import { useCelebrate } from '../../components/CelebrationToast.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { TechSheetPicker } from '../../components/TechSheetPicker.js';
import { techSheetFromSaved, commitTechSheet, openTechSheet, EMPTY_TECH_SHEET, type TechSheetState } from '../../lib/documents/techSheet.js';
import { useRecipeFavorites } from '../../lib/labels/recipeFavorites.js';
import { usePersistentWorkDraft } from '../../lib/drafts/usePersistentWorkDraft.js';
import { usePageActivity } from '../../lib/hooks/usePageActivity.js';
import {
  isPreparationFood,
  selectRecipeIngredientFoods,
} from '../../lib/recipeIngredientFoods.js';
import { hasRecipeComposition } from '../../lib/recipes/recipeComposition.js';

interface RecipeRow {
  id: string;
  tenant_id: string;
  business_id: string | null;
  name: string;
  description: string | null;
  yield_qty: number;
  yield_uom: string;
  yield_portions: number | null;
  menu_label: string | null;
  ingredients: Array<{ food_id: string; qty: number | null; uom: string | null; note?: string | null }>;
  allergens: string[];
  shelf_life_days: number | null;
  chilling_enabled: boolean;
  chilling_minutes: number | null;
  chilling_sign: 'positive' | 'negative' | null;
  temp_start_c: number | null;
  temp_end_c: number | null;
  freezing_enabled: boolean;
  ccp_type?: CcpType | null;
  default_shelf_life_minutes: number | null;
  shelf_life_minutes_opened?: number | null;
  shelf_life_minutes_thawed?: number | null;
  parent_recipe_ids: string[];
  // T-259: ricette base (semilavorati) con quantità. parent_recipe_ids ne è la
  // proiezione id (sincronizzata al salvataggio). Le ricette legacy hanno solo
  // parent_recipe_ids → si derivano con qty di default.
  parent_recipes?: Array<{ recipe_id: string; qty: number | null; uom: string | null }>;
  is_favorite?: boolean;
  // T-337: true = ricetta di solo calcolo costo → fuori dai picker operativi,
  // gestita solo da ristoratore/consulente/owner, resta nel modulo Food Cost.
  is_food_cost_only?: boolean;
  // T-441 (varianti dieta collettiva): una variante è una ricetta FIGLIA. Base =
  // entrambi null (come oggi). Una variante ha variant_of_recipe_id (id della
  // base) + diet_code. variant_base_fingerprint = fingerprint degli ingredienti
  // della base al momento del fork/allineamento → rileva la deriva.
  variant_of_recipe_id?: string | null;
  diet_code?: string | null;
  variant_base_fingerprint?: string | null;
  selling_price_cents?: number | null;
  // T-450 F1: override IVA % per il food cost del piatto (null = default locale).
  vat_rate?: number | null;
  // T-459: path bucket tech-sheets della scheda tecnica (PDF). null se assente.
  data_sheet_file_id?: string | null;
  archived_at: string | null;
}

interface FoodRow {
  id: string;
  name: string;
  default_uom: string;
  recipe_id?: string | null;
  allergens?: string[];
  archived_at?: string | null;
}

type ShelfUnit = 'ore' | 'giorni' | 'settimane' | 'mesi';
type RecipeEditorStep = 'base' | 'ingredients' | 'safety';

interface IngredientDraft {
  food_id: string;
  qty: string;
  uom: string;
}

// T-259: riga "ricetta base / semilavorato" nel draft, con quantità editabile
// (in proporzione alla resa della ricetta, come un ingrediente).
interface SubRecipeDraft {
  recipe_id: string;
  qty: string; // input raw
  uom: string; // unità della resa del semilavorato (read-only in UI)
}

interface DraftState {
  // Quando valorizzato, il modal sta MODIFICANDO una ricetta esistente
  // (op: 'update'), altrimenti CREA una nuova (op: 'insert').
  editId: string | null;
  baseVersion: number;
  name: string;
  description: string;
  yieldQty: string;
  yieldUom: string;
  yieldPortions: string;
  menuLabel: string;
  ingredients: IngredientDraft[];
  shelfLifeQty: string;
  shelfLifeUnit: ShelfUnit;
  chillingEnabled: boolean;
  chillingHours: string;
  chillingMinutes: string;
  chillingSign: 'positive' | 'negative';
  tempStart: string;
  tempEnd: string;
  freezingEnabled: boolean;
  openedShelfLifeQty: string;
  openedShelfLifeUnit: ShelfUnit;
  thawedShelfLifeQty: string;
  thawedShelfLifeUnit: ShelfUnit;
  // T-422: tipo controllo temperatura al cuore (cottura ≥74°C / servito freddo).
  ccpType: CcpType;
  parentRecipes: SubRecipeDraft[];
  // T-337: ricetta di solo food cost (non stampabile). Settabile solo da
  // ristoratore/consulente/owner.
  isFoodCostOnly: boolean;
  // T-450 F1: override IVA % del piatto per il food cost (vuoto = default locale).
  vatRate: string;
  // T-459: stato della scheda tecnica (PDF) — controllato dal parent, caricato
  // su Storage solo al Salva (offline-first, come la foto in FoodEditorModal).
  techSheet: TechSheetState;
}

// Sentinella per foodSearchIdx: il selettore "Scegli un alimento" è aperto
// per AGGIUNGERE un nuovo ingrediente (la riga viene creata solo dopo la
// scelta), non per modificare una riga esistente. -1 non indicizza mai
// draft.ingredients (undefined), quindi nessuna preselezione.
const ADD_INGREDIENT_IDX = -1;

const EMPTY_DRAFT: DraftState = {
  editId: null,
  baseVersion: 0,
  name: '',
  description: '',
  yieldQty: '1',
  yieldUom: 'porzioni',
  yieldPortions: '',
  menuLabel: '',
  ingredients: [],
  shelfLifeQty: '',
  shelfLifeUnit: 'giorni',
  chillingEnabled: false,
  chillingHours: '04',
  chillingMinutes: '00',
  chillingSign: 'negative',
  tempStart: '30',
  tempEnd: '-18',
  freezingEnabled: false,
  openedShelfLifeQty: '',
  openedShelfLifeUnit: 'giorni',
  thawedShelfLifeQty: '',
  thawedShelfLifeUnit: 'ore',
  ccpType: 'cottura',
  parentRecipes: [],
  isFoodCostOnly: false,
  vatRate: '',
  techSheet: EMPTY_TECH_SHEET,
};

// Scegli l'unità di misura "umana" per shelf-life partendo da minuti.
function minutesToShelfLife(min: number): { qty: string; unit: ShelfUnit } {
  if (min <= 0) return { qty: '', unit: 'giorni' };
  if (min % (30 * 24 * 60) === 0) return { qty: String(min / (30 * 24 * 60)), unit: 'mesi' };
  if (min % (7 * 24 * 60) === 0)  return { qty: String(min / (7 * 24 * 60)),  unit: 'settimane' };
  if (min % (24 * 60) === 0)      return { qty: String(min / (24 * 60)),      unit: 'giorni' };
  if (min % 60 === 0)             return { qty: String(min / 60),             unit: 'ore' };
  return { qty: String(Math.round(min / (24 * 60))), unit: 'giorni' };
}

function shelfLifeToMinutes(qty: number, unit: ShelfUnit): number {
  switch (unit) {
    case 'ore': return Math.round(qty * 60);
    case 'giorni': return Math.round(qty * 24 * 60);
    case 'settimane': return Math.round(qty * 7 * 24 * 60);
    case 'mesi': return Math.round(qty * 30 * 24 * 60);
  }
}

const newId = (): string => crypto.randomUUID();

// Rimuove accenti e abbassa: ricerca tollerante ("pere" trova "Però").
function normSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

export function RecipesPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { isActive, capture } = usePageActivity('/haccp/recipes', profile?.active_tenant_id ?? null);
  const editorLoadRequest = useRef(0);
  const saveInFlight = useRef(false);
  const canCreateFoods = canCreateFood(profile);
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { favoriteIds, toggleFavorite: toggleRecipeFavorite, ownerLabel: favoriteOwnerLabel } = useRecipeFavorites();
  const enqueueMaster = useEnqueueMaster();
  // T-130: il tenant gestisce le quantità nelle ricette? Se no, l'editor non
  // mostra/chiede qty/uom (ingredienti + semilavorati).
  const recipeQtyEnabled = useRecipeQuantitiesEnabled();
  const collettivaEnabled = useCollettivaEnabled();
  const [open, setOpen] = useState(false);
  const [editorStep, setEditorStep] = useState<RecipeEditorStep>('base');
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // FoodEditorModal: aperto inline per "crea nuovo alimento" senza uscire dalla
  // ricetta. `appendAfterSave` indica che il food appena creato deve essere
  // aggiunto subito come ingrediente al draft.
  const [foodEditorOpen, setFoodEditorOpen] = useState(false);
  const [foodEditorAppend, setFoodEditorAppend] = useState(false);
  const recipeNameRef = useRef<HTMLIonInputElement>(null);
  // T-441: snapshot serializzato del draft al momento del caricamento/reset.
  // Un effect confronta il draft corrente con questo baseline per derivare
  // isDirty senza instrumentare ogni singolo setDraft. Il campo `editId` è
  // escluso dal confronto (non è un dato editabile dall'utente).
  const draftBaselineRef = useRef<string>('');
  // Duplicazione ricetta: l'editor NON gestisce selling_price_cents (si edita
  // nel modulo Food Cost), ma la duplica deve portarsi dietro "tutte le info".
  // Lo conservo qui dalla ricetta sorgente e lo inietto nel patch di INSERT
  // (un update è un merge parziale lato server → non serve, un insert no).
  // Null = nuova ricetta vuota (non una duplica) → prezzo assente come sempre.
  const duplicateExtrasRef = useRef<{ selling_price_cents: number | null } | null>(null);
  // Indice ingrediente per cui è aperto il selettore-ricerca alimento.
  const [foodSearchIdx, setFoodSearchIdx] = useState<number | null>(null);
  const [parentRecipePicker, setParentRecipePicker] = useState(false);
  // Alert non-bloccante mostrato dopo salvataggio di una ricetta senza ingredienti.
  const [incompleteAlert, setIncompleteAlert] = useState<string | null>(null);
  // T-441: quale "vista" si sta editando nell'editor: 'base' oppure il diet_code
  // di una variante. L'editor edita UNA ricetta per volta (base O variante) →
  // salvataggio a riga singola, mai multi-riga non atomico (spec §5.2).
  const [variantView, setVariantView] = useState<'base' | string>('base');
  // T-441: modal "aggiungi variante dieta" (picker diete variantabili).
  const [addVariantOpen, setAddVariantOpen] = useState(false);
  // T-441: flag "il draft ha modifiche non ancora salvate". Impostato a true da
  // ogni edit del draft nell'editor; azzerato a ogni caricamento/salvataggio.
  const [isDirty, setIsDirty] = useState(false);
  // Id della ricetta BASE attualmente aperta; resta fisso passando alle varianti.
  const [baseEditId, setBaseEditId] = useState<string | null>(null);

  const recipeDraftPayload = useMemo(
    () => ({ draft, editorStep, variantView, baseEditId }),
    [baseEditId, draft, editorStep, variantView],
  );
  const recipeSharedDraftPayload = useMemo(
    () => ({
      ...recipeDraftPayload,
      draft: {
        ...recipeDraftPayload.draft,
        techSheet: { ...recipeDraftPayload.draft.techSheet, pending: [] },
      },
    }),
    [recipeDraftPayload],
  );
  const recipeWorkDraft = usePersistentWorkDraft({
    enabled: open || busy,
    accountId: profile?.id ?? null,
    tenantId: profile?.active_tenant_id ?? null,
    businessId: profile?.active_business_id ?? null,
    resourceKind: 'recipe',
    resourceKey: draft.editId ?? 'new',
    payload: recipeDraftPayload,
    sharedPayload: recipeSharedDraftPayload,
    onRestore: (saved) => {
      if (!saved || typeof saved !== 'object' || !('draft' in saved)) return;
      const restored = saved as {
        draft: DraftState;
        editorStep?: RecipeEditorStep;
        variantView?: string;
        baseEditId?: string | null;
      };
      setDraft(restored.draft);
      setEditorStep(restored.editorStep ?? 'base');
      setVariantView(restored.variantView ?? 'base');
      setBaseEditId(restored.baseEditId ?? restored.draft.editId ?? null);
      setToast('Bozza ricetta recuperata.');
    },
  });
  const recipeDraftLocked = recipeWorkDraft.status === 'locked';
  const recipeDraftReadOnly = recipeDraftLocked
    || recipeWorkDraft.status === 'idle'
    || recipeWorkDraft.status === 'acquiring';
  const recipeDraftFinalizable = recipeWorkDraft.status === 'editable';

  const [recipeQuery, setRecipeQuery] = useState('');
  // T-337: solo ristoratore/consulente/owner vedono e gestiscono le ricette
  // "solo food cost"; staff e osa no (stesso gate canEdit usato altrove).
  const canManageFoodCost =
    profile?.is_platform_owner === true
    || profile?.role === 'consulente'
    || profile?.role === 'ristoratore';
  // Vista attiva: ricette operative vs ricette di solo food cost.
  const [recipeView, setRecipeView] = useState<'operative' | 'foodcost'>('operative');
  // T-342: deep-link ?edit=<id> dal Food Cost → apre l'editor completo.
  const history = useHistory();
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  useEffect(() => {
    if (isActive) return;
    editorLoadRequest.current++;
    setPendingEditId(null);
    setOpen(false);
    setFoodEditorOpen(false);
    setFoodEditorAppend(false);
    setFoodSearchIdx(null);
    setParentRecipePicker(false);
    setAddVariantOpen(false);
    setErrorAlert(null);
    setIncompleteAlert(null);
  }, [isActive]);
  // Ricette attive (non archiviate), divise per scopo. Le food-cost-only sono
  // visibili solo a chi può gestirle. T-441: le VARIANTI (ricette figlie con
  // variant_of_recipe_id) sono nascoste dalla lista/picker — si gestiscono solo
  // dentro l'editor della loro base, via la tendina "Vista". I lookup by-id (es.
  // computedAllergens) usano `recipes` intero → restano inclusi.
  const activeRecipes = useMemo(
    () => recipes.filter((r) => !r.archived_at && r.variant_of_recipe_id == null),
    [recipes],
  );
  const operativeRecipes = useMemo(
    () => activeRecipes.filter((r) => !r.is_food_cost_only),
    [activeRecipes],
  );
  const foodCostRecipes = useMemo(
    () => activeRecipes.filter((r) => r.is_food_cost_only),
    [activeRecipes],
  );
  // Lista mostrata: il segment FoodCost è raggiungibile solo se canManageFoodCost.
  const visibleRecipes = useMemo(
    () => (canManageFoodCost && recipeView === 'foodcost' ? foodCostRecipes : operativeRecipes),
    [canManageFoodCost, recipeView, foodCostRecipes, operativeRecipes],
  );
  // C'è almeno una ricetta da mostrare (per scegliere tra intro e lista). Per
  // staff/osa contano solo le operative (non vedono mai le food-cost-only).
  const hasAnyRecipe = canManageFoodCost
    ? activeRecipes.length > 0
    : operativeRecipes.length > 0;
  // Filtro di ricerca per nome ricetta (accent/case-insensitive).
  const filteredRecipes = useMemo(() => {
    const q = normSearch(recipeQuery);
    if (!q) return visibleRecipes;
    return visibleRecipes.filter((r) => normSearch(r.name ?? '').includes(q));
  }, [visibleRecipes, recipeQuery]);
  const availableFoods = useMemo(
    () => selectRecipeIngredientFoods(foods),
    [foods],
  );

  // T-342: deep-link da Food Cost. useIonViewWillEnter (non solo useEffect)
  // perché Ionic tiene la pagina in cache nel router outlet → alla seconda
  // navigazione un effect-once non rifirerebbe. Legge ?edit=<id>, segna il
  // pendingEditId e pulisce subito il param (rientrando non riapre).
  useIonViewWillEnter(() => {
    const rid = new URLSearchParams(history.location.search).get('edit');
    if (rid) {
      setPendingEditId(rid);
      history.replace('/haccp/recipes');
    }
  });
  // Apre l'editor quando i master sono caricati e la ricetta esiste (gestisce il
  // caso in cui al view-enter i master non erano ancora pronti).
  useEffect(() => {
    if (!isActive || !pendingEditId) return;
    const r = recipes.find((x) => x.id === pendingEditId);
    if (!r) return;
    if (r.is_food_cost_only) setRecipeView('foodcost');
    void openEdit(r);
    setPendingEditId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, pendingEditId, recipes]);

  // Allergeni auto-aggregati dagli ingredienti food + dalle sub-ricette.
  // Reg. UE 1169/2011: gli allergeni di una ricetta che usa sub-ricette
  // ("ricette base") devono includere anche gli allergeni propagati dalla
  // catena annidata. Esploro fino a 5 livelli di profondità con cycle guard
  // per evitare loop in caso di dati malformati.
  const computedAllergens = useMemo(() => {
    const set = new Set<string>();
    for (const ing of draft.ingredients) {
      const food = foods.find((f) => f.id === ing.food_id);
      for (const a of food?.allergens ?? []) set.add(a);
    }
    const recipesById = new Map(recipes.map((r) => [r.id, r]));
    const visited = new Set<string>();
    const queue = draft.parentRecipes.map((p) => p.recipe_id);
    let depth = 0;
    while (queue.length && depth < 5) {
      const next: string[] = [];
      for (const rid of queue) {
        if (visited.has(rid)) continue;
        visited.add(rid);
        const sub = recipesById.get(rid);
        if (!sub) continue;
        for (const a of sub.allergens ?? []) set.add(a);
        for (const childId of sub.parent_recipe_ids ?? []) {
          if (!visited.has(childId)) next.push(childId);
        }
      }
      queue.length = 0;
      queue.push(...next);
      depth += 1;
    }
    return [...set];
  }, [draft.ingredients, draft.parentRecipes, foods, recipes]);

  // T-441: baseEditId resta FISSO quando si cambia "Vista" tra base e varianti,
  // mentre draft.editId segue la riga realmente mostrata.
  // La riga base corrente (dai master già sincronizzati).
  const baseRecipeRow = useMemo(
    () => (baseEditId ? recipes.find((r) => r.id === baseEditId) ?? null : null),
    [recipes, baseEditId],
  );
  // Varianti (ricette figlie) della base in editor: già sincronizzate → basta
  // filtrarle da `recipes`, nessuna query extra. Le archiviate sono escluse.
  const currentVariants = useMemo(
    () => recipes.filter((r) => r.variant_of_recipe_id === baseEditId && !r.archived_at),
    [recipes, baseEditId],
  );
  // T-441 (deriva): varianti il cui fingerprint atteso ≠ fingerprint attuale
  // degli ingredienti della base → la base è cambiata dopo la variante, va
  // rivista (allergeni/ingredienti). Set dei diet_code "da rivedere".
  const staleVariants = useMemo(() => {
    if (!baseRecipeRow) return new Set<string>();
    const fp = ingredientsFingerprint(baseRecipeRow.ingredients ?? []);
    return new Set(
      currentVariants
        .filter((v) => v.variant_base_fingerprint !== fp)
        .map((v) => v.diet_code ?? ''),
    );
  }, [currentVariants, baseRecipeRow]);

  // T-441: serializza i soli campi EDITABILI del draft (esclusi editId/baseVersion,
  // che identificano la riga, non il suo contenuto). Confrontato col baseline per
  // derivare isDirty.
  function serializeDraftContent(d: DraftState): string {
    const content: Omit<DraftState, 'editId' | 'baseVersion'> = {
      name: d.name,
      description: d.description,
      yieldQty: d.yieldQty,
      yieldUom: d.yieldUom,
      yieldPortions: d.yieldPortions,
      menuLabel: d.menuLabel,
      ingredients: d.ingredients,
      shelfLifeQty: d.shelfLifeQty,
      shelfLifeUnit: d.shelfLifeUnit,
      chillingEnabled: d.chillingEnabled,
      chillingHours: d.chillingHours,
      chillingMinutes: d.chillingMinutes,
      chillingSign: d.chillingSign,
      tempStart: d.tempStart,
      tempEnd: d.tempEnd,
      freezingEnabled: d.freezingEnabled,
      openedShelfLifeQty: d.openedShelfLifeQty,
      openedShelfLifeUnit: d.openedShelfLifeUnit,
      thawedShelfLifeQty: d.thawedShelfLifeQty,
      thawedShelfLifeUnit: d.thawedShelfLifeUnit,
      ccpType: d.ccpType,
      parentRecipes: d.parentRecipes,
      isFoodCostOnly: d.isFoodCostOnly,
      vatRate: d.vatRate,
      techSheet: d.techSheet,
    };
    return JSON.stringify(content);
  }

  // Marca isDirty quando il contenuto del draft diverge dal baseline caricato.
  // Così un cambio di Vista con modifiche pendenti può avvisare, senza dover
  // instrumentare ogni setDraft.
  useEffect(() => {
    setIsDirty(serializeDraftContent(draft) !== draftBaselineRef.current);
  }, [draft]);

  function reset(): void {
    editorLoadRequest.current++;
    draftBaselineRef.current = serializeDraftContent(EMPTY_DRAFT);
    duplicateExtrasRef.current = null;
    setDraft(EMPTY_DRAFT);
    setVariantView('base');
    setBaseEditId(null);
    setIsDirty(false);
    setEditorStep('base');
  }

  // Esiste già (per questo tenant) una ricetta ATTIVA, non-variante, con questo
  // nome? Confronto accent/case-insensitive. `excludeId` esclude la ricetta che
  // si sta modificando (altrimenti si bloccherebbe da sé). Usato dal gate
  // anti-duplicato al salvataggio e dal generatore del nome "Copia di …".
  const recipeNameExists = useCallback(
    (name: string, excludeId: string | null): boolean => {
      const target = normSearch(name);
      if (!target) return false;
      return activeRecipes.some(
        (r) => r.id !== excludeId && normSearch(r.name ?? '') === target,
      );
    },
    [activeRecipes],
  );

  // Propone un nome libero "Copia di X" (→ "Copia di X (2)", …) che non collida
  // con una ricetta esistente e stia nel limite di 200 caratteri del campo nome.
  const suggestCopyName = useCallback(
    (baseName: string): string => {
      const MAX = 200;
      const stem = `Copia di ${baseName}`;
      const first = stem.slice(0, MAX);
      if (!recipeNameExists(first, null)) return first;
      for (let n = 2; n <= 99; n++) {
        const suffix = ` (${n})`;
        const cand = stem.slice(0, MAX - suffix.length) + suffix;
        if (!recipeNameExists(cand, null)) return cand;
      }
      return first;
    },
    [recipeNameExists],
  );

  // Duplica una ricetta: apre l'editor come NUOVA ricetta (editId=null → save()
  // fa un insert), con TUTTI i campi copiati dalla sorgente e il nome
  // pre-compilato con un "Copia di …" libero. Non scrive nulla finché l'utente
  // non salva: onDidPresent mette il focus sul campo nome ed evidenzia il testo
  // così può rinominarla subito. Una duplica è sempre una ricetta BASE
  // indipendente (i campi variante vengono azzerati da buildDraftFromRecipe +
  // variantView 'base').
  function duplicateRecipe(r: RecipeRow): void {
    if (busy) return;
    const dup: DraftState = {
      ...buildDraftFromRecipe(r, 0),
      editId: null,
      baseVersion: 0,
      name: suggestCopyName(r.name),
    };
    duplicateExtrasRef.current = { selling_price_cents: r.selling_price_cents ?? null };
    draftBaselineRef.current = serializeDraftContent(dup);
    setBaseEditId(null);
    setVariantView('base');
    setDraft(dup);
    setIsDirty(false);
    setEditorStep('base');
    setOpen(true);
  }

  // T-616: la stella appartiene all'account (o alla persona selezionata sul
  // tablet), non modifica piu la ricetta condivisa dal ristorante.
  async function toggleFavorite(r: RecipeRow): Promise<void> {
    try {
      await toggleRecipeFavorite(r.id);
    } catch (err) {
      setToast(`Errore: ${toUserMessage(err)}`);
    }
  }

  // Costruisce lo stato del draft da una ricetta esistente + la sua version.
  // Puro (nessun side-effect): riusato sia da openEdit (apertura editor) sia da
  // loadRecipeIntoDraft (cambio Vista base⇄variante). T-441.
  function buildDraftFromRecipe(r: RecipeRow, version: number): DraftState {
    // Shelf-life: prendi i minuti se presenti (più precisi), altrimenti i giorni.
    const shelfMin = r.default_shelf_life_minutes
      ?? (r.shelf_life_days ? r.shelf_life_days * 24 * 60 : 0);
    const shelf = minutesToShelfLife(shelfMin);
    const openedShelf = minutesToShelfLife(r.shelf_life_minutes_opened ?? 0);
    const thawedShelf = minutesToShelfLife(r.shelf_life_minutes_thawed ?? 0);
    const chillingMin = r.chilling_minutes ?? 0;
    const ch = Math.floor(chillingMin / 60).toString().padStart(2, '0');
    const cm = (chillingMin % 60).toString().padStart(2, '0');
    return {
      editId: r.id,
      baseVersion: version,
      name: r.name,
      description: r.description ?? '',
      // T-242: per le ricette in "porzioni" il campo unico "Quantità prodotta"
      // mostra il numero di porzioni. Le ricette T-229 legacy possono avere
      // yield_qty al default (1) ma il vero numero di porzioni in yield_portions
      // (era quello su cui scalava il flusso a porzioni): in quel caso mostro
      // yield_portions, così al salvataggio — che ri-deriva yield_portions da
      // yield_qty — il dato non viene azzerato e lo scaling resta corretto.
      yieldQty: String(
        (r.yield_uom ?? 'porzioni') === 'porzioni' && r.yield_portions != null && r.yield_portions > 0
          ? r.yield_portions
          : (r.yield_qty ?? 1),
      ),
      yieldUom: r.yield_uom ?? 'porzioni',
      yieldPortions: r.yield_portions != null ? String(r.yield_portions) : '',
      menuLabel: r.menu_label ?? '',
      ingredients: (r.ingredients ?? []).map((ing) => ({
        food_id: ing.food_id,
        qty: ing.qty == null ? '' : String(ing.qty),
        uom: ing.uom ?? '',
      })),
      shelfLifeQty: shelf.qty,
      shelfLifeUnit: shelf.unit,
      chillingEnabled: r.chilling_enabled,
      chillingHours: chillingMin > 0 ? ch : '01',
      chillingMinutes: chillingMin > 0 ? cm : '30',
      chillingSign: r.chilling_sign ?? 'negative',
      tempStart: r.temp_start_c != null ? String(r.temp_start_c) : '90',
      tempEnd: r.temp_end_c != null ? String(r.temp_end_c) : '-18',
      freezingEnabled: r.freezing_enabled,
      openedShelfLifeQty: openedShelf.qty,
      openedShelfLifeUnit: openedShelf.unit,
      thawedShelfLifeQty: thawedShelf.qty,
      thawedShelfLifeUnit: thawedShelf.unit,
      ccpType: (r.ccp_type as CcpType) ?? 'cottura',
      // T-259: preferisci parent_recipes (con qty); fallback alle ricette legacy
      // che hanno solo parent_recipe_ids (qty di default = 1, unità = resa).
      parentRecipes: (r.parent_recipes && r.parent_recipes.length > 0)
        ? r.parent_recipes.map((p) => ({ recipe_id: p.recipe_id, qty: p.qty == null ? '' : String(p.qty), uom: p.uom ?? '' }))
        : (r.parent_recipe_ids ?? []).map((rid) => {
            const sub = recipes.find((x) => x.id === rid);
            return { recipe_id: rid, qty: '1', uom: sub?.yield_uom ?? 'porz' };
          }),
      isFoodCostOnly: r.is_food_cost_only ?? false,
      vatRate: r.vat_rate != null ? String(r.vat_rate) : '',
      // T-459: scheda tecnica dal path salvato sulla ricetta (null se assente).
      techSheet: techSheetFromSaved(r.data_sheet_file_id ?? null),
    };
  }

  // T-441: carica una ricetta (base O variante) nel draft dell'editor, leggendo
  // la version locale (per l'OCC su sync-push). Azzera il flag "modifiche non
  // salvate": è appena stata caricata. NON tocca baseEditId (resta la base) né
  // apre/chiude il modal. Usato dal cambio Vista.
  const loadRecipeIntoDraft = useCallback(async (r: RecipeRow): Promise<boolean> => {
    const isCurrent = capture();
    const request = ++editorLoadRequest.current;
    const db = await openLocalDb(null);
    const res = await db.query(
      `SELECT version FROM masters_local WHERE table_name = 'recipes' AND id = ?`,
      [r.id],
    );
    if (!isCurrent() || request !== editorLoadRequest.current) return false;
    const version = (res.values?.[0] as { version: number } | undefined)?.version ?? 0;
    const next = buildDraftFromRecipe(r, version);
    draftBaselineRef.current = serializeDraftContent(next);
    setDraft(next);
    setIsDirty(false);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture, recipes]);

  // Apri modal in modalità modifica: precompila tutti i campi dal record
  // esistente (sempre la BASE) e imposta la Vista su 'base'. Legge la version
  // corrente dal cache locale (per OCC su sync-push).
  async function openEdit(r: RecipeRow): Promise<void> {
    if (busy) return;
    if (!await loadRecipeIntoDraft(r)) return;
    setBaseEditId(r.id);
    setVariantView('base');
    setEditorStep('base');
    setOpen(true);
  }

  // T-441: cambio della tendina "Vista". '__add__' apre il picker per creare una
  // nuova variante; 'base' ricarica la base; un diet_code carica la variante
  // corrispondente. Se ci sono modifiche non salvate nella vista corrente,
  // chiede conferma (le viste sono ricette a sé, salvate singolarmente).
  const onSwitchVariantView = useCallback(
    async (next: string): Promise<void> => {
      if (next === '__add__') {
        setAddVariantOpen(true);
        return;
      }
      if (next === variantView) return;
      if (isDirty) {
        const ok = await confirmDialog(
          'Hai modifiche non salvate in questa vista. Cambiare vista le scarterà. Continuare?',
          { okText: 'Cambia vista', danger: true },
        );
        if (!ok) return;
      }
      if (next === 'base') {
        if (baseRecipeRow && !await loadRecipeIntoDraft(baseRecipeRow)) return;
        setVariantView('base');
      } else {
        const v = currentVariants.find((x) => x.diet_code === next);
        if (!v) return;
        if (!await loadRecipeIntoDraft(v)) return;
        setVariantView(next);
      }
    },
    [variantView, isDirty, baseRecipeRow, currentVariants, loadRecipeIntoDraft],
  );

  // T-441: crea una nuova variante = ricetta FIGLIA, copia della base, marcata
  // con variant_of_recipe_id + diet_code. is_favorite/is_food_cost_only/prezzo
  // forzati a false/null (una variante non va in vetrina né in food cost — spec
  // §8). Il fingerprint cattura lo stato attuale della base (per la deriva).
  // base.id è noto client-side → variant_of_recipe_id valido senza round-trip;
  // la base esiste già in editor → ordine base-prima-del-figlio garantito.
  const createVariant = useCallback(
    async (code: string): Promise<void> => {
      const isCurrent = capture();
      const base = baseRecipeRow;
      const tenantId = profile?.active_tenant_id;
      if (!base || !tenantId) return;
      // Guard: non duplicare una variante già definita per questa dieta.
      if (currentVariants.some((v) => v.diet_code === code)) {
        setVariantView(code);
        setAddVariantOpen(false);
        const existing = currentVariants.find((v) => v.diet_code === code);
        if (existing) await loadRecipeIntoDraft(existing);
        return;
      }
      const variantId = newId();
      const patch = {
        id: variantId,
        tenant_id: tenantId,
        business_id: base.business_id ?? null,
        name: `${base.name} (${collettivaDietLabel(code)})`,
        description: base.description ?? null,
        yield_qty: base.yield_qty,
        yield_uom: base.yield_uom,
        yield_portions: base.yield_portions,
        ingredients: base.ingredients ?? [],
        process_steps: [],
        allergens: base.allergens ?? [],
        allergens_auto_computed: true,
        shelf_life_days: base.shelf_life_days,
        chilling_enabled: base.chilling_enabled,
        chilling_minutes: base.chilling_minutes,
        chilling_sign: base.chilling_sign,
        temp_start_c: base.temp_start_c,
        temp_end_c: base.temp_end_c,
        freezing_enabled: base.freezing_enabled,
        ccp_type: base.ccp_type ?? 'cottura',
        default_shelf_life_minutes: base.default_shelf_life_minutes,
        shelf_life_minutes_opened: base.shelf_life_minutes_opened ?? null,
        shelf_life_minutes_thawed: base.shelf_life_minutes_thawed ?? null,
        parent_recipes: base.parent_recipes ?? [],
        parent_recipe_ids: base.parent_recipe_ids ?? [],
        // …ma marcala come variante e togli vetrina/food-cost/prezzo.
        variant_of_recipe_id: base.id,
        diet_code: code,
        variant_base_fingerprint: ingredientsFingerprint(base.ingredients ?? []),
        is_favorite: false,
        is_food_cost_only: false,
        selling_price_cents: null,
        menu_label: null,
      };
      setBusy(true);
      try {
        await enqueueMaster.mutateAsync({
          op: 'insert',
          table: 'recipes',
          id: variantId,
          patch,
          base_version: 0,
        });
        if (!isCurrent()) return;
        setAddVariantOpen(false);
        setVariantView(code);
        // Apri subito la nuova variante per modificarla.
        await loadRecipeIntoDraft({ ...(base as RecipeRow), ...patch } as RecipeRow);
      } catch (err) {
        if (isCurrent()) setErrorAlert(`Creazione variante fallita: ${toUserMessage(err)}`);
      } finally {
        setBusy(false);
      }
    },
    [baseRecipeRow, capture, profile, currentVariants, enqueueMaster, loadRecipeIntoDraft],
  );

  // T-441: elimina (archivia) la variante correntemente in vista. Torna alla base.
  const removeVariant = useCallback(async (): Promise<void> => {
    const isCurrent = capture();
    const v = currentVariants.find((x) => x.diet_code === variantView);
    const tenantId = profile?.active_tenant_id;
    if (!v || !tenantId) return;
    setBusy(true);
    try {
      const db = await openLocalDb(null);
      const res = await db.query(
        `SELECT version FROM masters_local WHERE table_name = 'recipes' AND id = ?`,
        [v.id],
      );
      const version = (res.values?.[0] as { version: number } | undefined)?.version ?? 0;
      await enqueueMaster.mutateAsync({
        op: 'archive',
        table: 'recipes',
        id: v.id,
        patch: { id: v.id, tenant_id: tenantId },
        base_version: version,
      });
      if (!isCurrent()) return;
      setToast('Variante eliminata');
      setVariantView('base');
      if (baseRecipeRow) await loadRecipeIntoDraft(baseRecipeRow);
    } catch (err) {
      if (isCurrent()) setErrorAlert(`Eliminazione variante fallita: ${toUserMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }, [capture, currentVariants, variantView, profile, enqueueMaster, baseRecipeRow, loadRecipeIntoDraft]);

  // Archivio (soft-delete) della BASE dalla vista modifica. T-441: archivia a
  // CASCATA anche le sue varianti (ricette figlie), così non restano
  // varianti-zombie orfane (spec §5.5). Il pulsante è visibile solo in vista
  // 'base' (in vista variante c'è "Elimina variante").
  async function archiveRecipe(): Promise<void> {
    if (!await recipeWorkDraft.assertLease()) {
      setToast('Impossibile confermare: la bozza non è più riservata a questo dispositivo.');
      return;
    }
    const baseId = baseEditId ?? draft.editId;
    if (!baseId) return;
    const tenantId = profile?.active_tenant_id;
    if (!tenantId) return;
    setBusy(true);
    try {
      // Prima le varianti figlie (leggo la loro version locale per l'OCC).
      const db = await openLocalDb(null);
      for (const v of currentVariants) {
        const res = await db.query(
          `SELECT version FROM masters_local WHERE table_name = 'recipes' AND id = ?`,
          [v.id],
        );
        const vv = (res.values?.[0] as { version: number } | undefined)?.version ?? 0;
        await enqueueMaster.mutateAsync({
          op: 'archive',
          table: 'recipes',
          id: v.id,
          patch: { id: v.id, tenant_id: tenantId },
          base_version: vv,
        });
      }
      // Poi la base.
      await enqueueMaster.mutateAsync({
        op: 'archive',
        table: 'recipes',
        id: baseId,
        patch: { id: baseId, tenant_id: tenantId },
        base_version: draft.baseVersion,
      });
      await recipeWorkDraft.complete();
      setToast(currentVariants.length > 0 ? 'Ricetta e varianti archiviate' : 'Ricetta archiviata');
      setOpen(false);
      reset();
    } catch (err) {
      setErrorAlert(`Archiviazione fallita: ${toUserMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  function addIngredient(): void {
    if (availableFoods.length === 0) {
      // Niente alimenti in magazzino: apri direttamente l'editor di creazione
      // food, e quando salvato aggiungilo subito come ingrediente.
      setFoodEditorAppend(true);
      setFoodEditorOpen(true);
      return;
    }
    // Vai DIRITTO al selettore "Scegli un alimento": la riga ingrediente
    // viene creata solo dopo la scelta (vedi onSelect del SearchPickerModal).
    // Niente più riga pre-compilata da ri-toccare.
    setFoodSearchIdx(ADD_INGREDIENT_IDX);
  }

  // Apre il FoodEditor in modalità "crea e poi aggiungi alla ricetta corrente".
  function openInlineFoodCreate(): void {
    setFoodEditorAppend(true);
    setFoodEditorOpen(true);
  }

  // Callback dopo che FoodEditorModal ha salvato il nuovo alimento.
  // Se foodEditorAppend = true → aggiunge l'alimento come nuovo ingrediente
  // al draft della ricetta. Altrimenti è solo creazione standalone.
  function handleFoodCreated(food: FoodEditorValue): void {
    if (foodEditorAppend && food.id) {
      setDraft((d) => ({
        ...d,
        ingredients: [
          ...d.ingredients,
          { food_id: food.id, qty: '1', uom: food.default_uom || 'pz' },
        ],
      }));
      setToast(`"${food.name}" aggiunto agli ingredienti`);
    } else {
      celebrate('Alimento creato');
    }
    setFoodEditorAppend(false);
  }

  function removeIngredient(idx: number): void {
    setDraft((d) => ({ ...d, ingredients: d.ingredients.filter((_, i) => i !== idx) }));
  }

  // T-259: il picker conferma la LISTA di id selezionati. Riconcilio col draft:
  // mantengo qty/uom degli id già presenti, aggiungo i nuovi con qty di default
  // e l'unità = resa del semilavorato, scarto quelli deselezionati. Ordino come
  // arrivano dal picker (selezionati-primi).
  function confirmSubRecipes(ids: string[]): void {
    setDraft((d) => {
      const prev = new Map(d.parentRecipes.map((p) => [p.recipe_id, p]));
      const next: SubRecipeDraft[] = ids.map((rid) => {
        const existing = prev.get(rid);
        if (existing) return existing;
        const sub = recipes.find((x) => x.id === rid);
        return { recipe_id: rid, qty: '1', uom: sub?.yield_uom ?? 'porz' };
      });
      return { ...d, parentRecipes: next };
    });
  }

  function patchSubRecipeQty(recipeId: string, qty: string): void {
    setDraft((d) => ({
      ...d,
      parentRecipes: d.parentRecipes.map((p) => (p.recipe_id === recipeId ? { ...p, qty } : p)),
    }));
  }

  function removeSubRecipe(recipeId: string): void {
    setDraft((d) => ({ ...d, parentRecipes: d.parentRecipes.filter((p) => p.recipe_id !== recipeId) }));
  }

  function patchIngredient(idx: number, patch: Partial<IngredientDraft>): void {
    setDraft((d) => {
      const ings = d.ingredients.slice();
      ings[idx] = { ...ings[idx], ...patch };
      // Auto-update UoM se cambia food (prendi default_uom).
      if (patch.food_id) {
        const food = foods.find((f) => f.id === patch.food_id);
        if (food) ings[idx].uom = food.default_uom;
      }
      return { ...d, ingredients: ings };
    });
  }

  async function save(): Promise<void> {
    if (busy || saveInFlight.current) return;
    saveInFlight.current = true;
    const isCurrent = capture();
    setBusy(true);
    try {
      await saveRecipe();
    } catch (error) {
      if (isCurrent()) setErrorAlert(`Salvataggio fallito: ${toUserMessage(error)}`);
    } finally {
      saveInFlight.current = false;
      setBusy(false);
    }
  }

  async function saveRecipe(): Promise<void> {
    const isCurrent = capture();
    if (!recipeDraftFinalizable) {
      setToast(recipeDraftLocked
        ? 'Questa ricetta è già in modifica su un altro dispositivo.'
        : 'Attendi il salvataggio della bozza condivisa prima di confermare.');
      return;
    }
    if (!await recipeWorkDraft.assertLease()) {
      setToast('Impossibile confermare: la bozza non è più riservata a questo dispositivo.');
      return;
    }
    const tenantId = profile?.active_tenant_id;
    const businessId = profile?.active_business_id ?? null;
    if (!tenantId) {
      setErrorAlert('Tenant non attivo. Esci e rientra nell\'app.');
      return;
    }
    if (draft.name.trim().length < 1) {
      setToast('Nome ricetta obbligatorio');
      return;
    }
    // Gate anti-duplicato: due ricette non possono avere lo stesso nome (per
    // tenant, tra le ricette attive non-variante). Scatta solo se il nome è
    // NUOVO o CAMBIATO rispetto alla riga salvata: così non blocca il salvataggio
    // di modifiche a ricette omonime pre-esistenti (dati legacy). Le varianti
    // (viste ≠ 'base') hanno nomi auto-generati "X (dieta)" e sono escluse.
    if (variantView === 'base') {
      const original = draft.editId ? recipes.find((x) => x.id === draft.editId) : null;
      const nameChanged = !original || normSearch(original.name ?? '') !== normSearch(draft.name.trim());
      if (nameChanged && recipeNameExists(draft.name.trim(), draft.editId)) {
        setToast('Esiste già una ricetta con questo nome. Scegline uno diverso.');
        return;
      }
    }
    const yieldQty = Number(draft.yieldQty);
    if (!Number.isFinite(yieldQty) || yieldQty <= 0) {
      setToast('Quantità prodotta non valida');
      return;
    }
    // T-242: le porzioni prodotte si inseriscono UNA volta sola, nel campo
    // "Quantità prodotta" qui sopra. Se l'unità è "porzioni", yield_qty È già il
    // numero di porzioni → lo derivo (niente seconda domanda, niente doppio
    // campo). Solo per unità diverse (kg/l/...) chiedo a parte quante porzioni
    // produce il batch, perché lì è un'informazione genuinamente aggiuntiva
    // (es. 5 kg = 50 porzioni) e non un doppione.
    const yieldUomNorm = draft.yieldUom.trim() || 'porzioni';
    let yieldPortions: number | null = null;
    if (yieldUomNorm === 'porzioni') {
      yieldPortions = Math.max(1, Math.round(yieldQty));
    } else if (draft.yieldPortions.trim()) {
      const yp = Number(draft.yieldPortions.trim());
      if (!Number.isInteger(yp) || yp <= 0) {
        setToast('Porzioni prodotte non valide');
        return;
      }
      yieldPortions = yp;
    }
    // T-450 F1: aliquota IVA override del piatto (vuoto = default locale = null).
    let vatRate: number | null = null;
    if (draft.vatRate.trim()) {
      const vr = Number(draft.vatRate.trim().replace(',', '.'));
      if (!Number.isFinite(vr) || vr < 0) {
        setToast('Aliquota IVA non valida');
        return;
      }
      vatRate = vr;
    }
    // Valida ingredienti. T-130: se il tenant NON gestisce le quantità, qty/uom
    // restano null (l'editor non le ha chieste) e non si valida la dose.
    const validIngredients: Array<{ food_id: string; qty: number | null; uom: string | null }> = [];
    for (let i = 0; i < draft.ingredients.length; i++) {
      const ing = draft.ingredients[i];
      if (!ing.food_id) { setToast(`Ingrediente ${i + 1}: alimento mancante`); return; }
      const ingFood = foods.find((f) => f.id === ing.food_id);
      if (isPreparationFood(ingFood)) {
        const preparation = recipes.find((recipe) => recipe.id === ingFood?.recipe_id);
        const preparationName = preparation?.name ?? ingFood?.name ?? `Ingrediente ${i + 1}`;
        setToast(
          `"${preparationName}" è una preparazione: rimuovila da Ingredienti e aggiungila in Ricette base (semi-lavorati).`,
        );
        return;
      }
      if (!recipeQtyEnabled) {
        validIngredients.push({ food_id: ing.food_id, qty: null, uom: null });
        continue;
      }
      const q = Number(ing.qty);
      if (!Number.isFinite(q) || q <= 0) { setToast(`Ingrediente ${i + 1}: quantità non valida`); return; }
      // T-221: l'unità salvata è SEMPRE quella di carico merce del food
      // (default_uom) — anche su ricette vecchie con uom libera divergente.
      validIngredients.push({ food_id: ing.food_id, qty: q, uom: ingFood?.default_uom || ing.uom || 'pz' });
    }

    // T-259/T-130: semilavorati con quantità (in proporzione alla resa). Quando
    // il tenant non gestisce le quantità, qty/uom = null e non si valida.
    const validSubRecipes: Array<{ recipe_id: string; qty: number | null; uom: string | null }> = [];
    for (const sr of draft.parentRecipes) {
      const sub = recipes.find((x) => x.id === sr.recipe_id);
      if (!recipeQtyEnabled) {
        validSubRecipes.push({ recipe_id: sr.recipe_id, qty: null, uom: null });
        continue;
      }
      const q = Number(sr.qty);
      if (!Number.isFinite(q) || q <= 0) {
        setToast(`Semilavorato "${sub?.name ?? sr.recipe_id}": quantità non valida`);
        return;
      }
      validSubRecipes.push({ recipe_id: sr.recipe_id, qty: q, uom: sr.uom || sub?.yield_uom || 'porz' });
    }

    let chillingMinutes: number | null = null;
    let chillingSign: 'positive' | 'negative' | null = null;
    let tempStart: number | null = null;
    let tempEnd: number | null = null;
    if (draft.chillingEnabled) {
      const h = parseInt(draft.chillingHours, 10);
      const m = parseInt(draft.chillingMinutes, 10);
      if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0 || m > 59) {
        setToast('Tempo abbattimento non valido');
        return;
      }
      chillingMinutes = h * 60 + m;
      chillingSign = draft.chillingSign;
      tempStart = draft.tempStart === '' ? null : Number(draft.tempStart);
      tempEnd = draft.tempEnd === '' ? null : Number(draft.tempEnd);
    }
    let defaultShelfLifeMin: number | null = null;
    let shelfLifeDays: number | null = null;
    if (draft.shelfLifeQty.trim()) {
      const q = Number(draft.shelfLifeQty);
      if (!Number.isFinite(q) || q <= 0) {
        setToast('Shelf-life non valida');
        return;
      }
      defaultShelfLifeMin = shelfLifeToMinutes(q, draft.shelfLifeUnit);
      shelfLifeDays = Math.max(1, Math.round(defaultShelfLifeMin / (24 * 60)));
    }
    const openedShelfQty = draft.openedShelfLifeQty.trim();
    const thawedShelfQty = draft.thawedShelfLifeQty.trim();
    const openedShelfLifeMin = openedShelfQty
      ? shelfLifeToMinutes(Number(openedShelfQty), draft.openedShelfLifeUnit)
      : null;
    const thawedShelfLifeMin = thawedShelfQty
      ? shelfLifeToMinutes(Number(thawedShelfQty), draft.thawedShelfLifeUnit)
      : null;
    if (
      (openedShelfQty && (!Number.isFinite(Number(openedShelfQty)) || Number(openedShelfQty) <= 0))
      || (thawedShelfQty && (!Number.isFinite(Number(thawedShelfQty)) || Number(thawedShelfQty) <= 0))
    ) {
      setToast('Shelf life dopo apertura o scongelamento non valida');
      return;
    }

    // T-441: la vista corrente decide quale RIGA si salva. Se è una variante,
    // l'id/version sono già quelli della variante (loadRecipeIntoDraft ha settato
    // draft.editId/baseVersion su di essa); si preservano variant_of_recipe_id +
    // diet_code e si aggiorna il fingerprint atteso ALLA BASE ATTUALE (così, dopo
    // aver rivisto e salvato la variante, non è più "da rivedere"). In vista base,
    // si scrive esplicitamente variant_of_recipe_id: null (una base non è variante).
    const savingVariant = variantView !== 'base';
    const variantRow = savingVariant
      ? currentVariants.find((v) => v.diet_code === variantView)
      : null;
    const isEdit = !!draft.editId;
    const id = isEdit ? draft.editId! : newId();
    const variantFields = savingVariant
      ? {
          variant_of_recipe_id: baseEditId ?? variantRow?.variant_of_recipe_id ?? null,
          diet_code: variantView,
          variant_base_fingerprint: ingredientsFingerprint(baseRecipeRow?.ingredients ?? []),
          is_favorite: false,
          is_food_cost_only: false,
          selling_price_cents: null,
          vat_rate: null,
        }
      : { variant_of_recipe_id: null, diet_code: null };
    // Duplicazione: porta il prezzo di vendita della sorgente nell'INSERT (un
    // update è un merge parziale server-side, un insert scriverebbe null). Solo
    // per una ricetta base creata via "Duplica"; una nuova vuota resta a null.
    const dupFields = (!isEdit && !savingVariant && duplicateExtrasRef.current)
      ? { selling_price_cents: duplicateExtrasRef.current.selling_price_cents }
      : {};
    try {
      // T-459: carica la scheda tecnica (PDF) su Storage SOLO ora, al Salva
      // (offline-first, come la foto): pending → upsert e ritorna il path;
      // removed → elimina e ritorna null; invariata → ritorna il path esistente.
      // Un fallimento dell'upload NON deve abortire il salvataggio della ricetta
      // (che va comunque in coda offline): fallback al path esistente + toast non
      // bloccante, esattamente come FoodEditorModal per la foto.
      let dataSheetPath: string | null = draft.techSheet.path;
      try {
        dataSheetPath = await commitTechSheet('recipes', tenantId, id, draft.techSheet);
      } catch (err) {
        setToast(`Scheda tecnica non caricata: ${toUserMessage(err)}`);
      }
      await enqueueMaster.mutateAsync({
        op: isEdit ? 'update' : 'insert',
        table: 'recipes',
        id,
        patch: {
          id,
          tenant_id: tenantId,
          business_id: businessId,
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          yield_qty: yieldQty,
          yield_uom: yieldUomNorm,
          yield_portions: yieldPortions,
          menu_label: savingVariant ? null : (draft.menuLabel.trim() || null),
          ingredients: validIngredients,
          process_steps: [],
          allergens: computedAllergens,
          allergens_auto_computed: true,
          shelf_life_days: shelfLifeDays,
          chilling_enabled: draft.chillingEnabled,
          chilling_minutes: chillingMinutes,
          chilling_sign: chillingSign,
          temp_start_c: tempStart,
          temp_end_c: tempEnd,
          freezing_enabled: draft.freezingEnabled,
          ccp_type: draft.ccpType,
          default_shelf_life_minutes: defaultShelfLifeMin,
          shelf_life_minutes_opened: openedShelfLifeMin,
          shelf_life_minutes_thawed: thawedShelfLifeMin,
          // T-259: parent_recipes = sorgente di verità (con qty); parent_recipe_ids
          // = proiezione id sincronizzata (allergeni + retro-compat).
          parent_recipes: validSubRecipes,
          parent_recipe_ids: validSubRecipes.map((p) => p.recipe_id),
          // T-337: ricetta di solo food cost (esclusa dai picker operativi). Una
          // variante non è mai food-cost (vedi variantFields sotto).
          is_food_cost_only: savingVariant ? false : draft.isFoodCostOnly,
          // T-450 F1: override IVA del piatto per il food cost (null = default locale).
          vat_rate: vatRate,
          // T-459: path della scheda tecnica (PDF) su bucket tech-sheets.
          data_sheet_file_id: dataSheetPath,
          // T-441: campi variante (o azzeramento in vista base).
          ...variantFields,
          // Duplicazione: prezzo di vendita ereditato dalla sorgente (solo insert).
          ...dupFields,
        },
        base_version: isEdit ? draft.baseVersion : 0,
      });
      await recipeWorkDraft.complete();
      setOpen(false);
      reset();
      if (!isCurrent()) return;
      // Alert non-bloccante: l'utente può salvare una ricetta davvero vuota,
      // ma deve sapere che è incompleta. Una preparazione base è già una
      // composizione valida anche senza ingredienti diretti.
      // Sostituisce il vecchio toast confermativo: se incompleta vince l'alert,
      // se completa vince il toast.
      if (!hasRecipeComposition({ ingredients: validIngredients, parent_recipes: validSubRecipes })) {
        setIncompleteAlert(
          isEdit
            ? 'Ricetta aggiornata, ma non ha ingredienti né preparazioni base. Aprila di nuovo e aggiungili quando vuoi.'
            : 'Ricetta creata, ma non ha ancora ingredienti né preparazioni base. Aprila di nuovo per aggiungerli.'
        );
      } else {
        celebrate(isEdit ? 'Ricetta aggiornata' : 'Ricetta creata');
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message || JSON.stringify(err) || 'Errore sconosciuto';
      console.error('[recipes:save] FAILED', err);
      if (isCurrent()) setErrorAlert(`Salvataggio fallito.\n\nDettaglio: ${msg}\n\n(Apri F12 → Console per più info)`);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>{t('haccp.recipes', 'Ricette')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="te-page">
        <div style={{ padding: '12px 16px 96px' }}>
          {!hasAnyRecipe
            && !isIntroDismissed('recipes', profile?.id ?? null, profile?.active_business_id ?? null) ? (
            <FeatureIntroBlock
              featureKey="recipes"
              icon="📖"
              title="Ricette e semilavorati"
              description={
                <>
                  Le <b>ricette</b> sono il legame tra magazzino e produzione:
                  per ogni piatto definisci gli ingredienti (foods o
                  semilavorati) e le quantità. Quando produci, il sistema
                  scarica automaticamente dal magazzino e crea le etichette
                  con allergeni e tracciabilità completa.
                  <br /><br />
                  Le ricette possono includere <b>semilavorati</b> (es. base
                  pizza, fondo bruno, marinata): un semilavorato è una
                  ricetta che a sua volta è ingrediente di un'altra. Gli
                  allergeni si propagano automaticamente per tutta la
                  catena. Indispensabile per il Manuale HACCP e per le
                  etichette obbligatorie Reg. UE 1169/2011.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi su come creare ricette e semilavorati in TrackEAT, mi puoi aiutare?"
              startLabel="Crea la prima ricetta"
              onStart={() => { if (!busy) setOpen(true); }}
              userId={profile?.id ?? null}
              businessId={profile?.active_business_id ?? null}
              flush
            />
          ) : !hasAnyRecipe ? (
            <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              Nessuna ricetta. Tocca + per crearne una.
            </div>
          ) : (
            <>
              <div className="te-screen-intro">
                <div>
                  <h1>Ricette e semilavorati</h1>
                  <p>Cerca una ricetta o creane una nuova. La compilazione è divisa in dati base, ingredienti e sicurezza HACCP.</p>
                </div>
                <IonButton disabled={busy} onClick={() => { reset(); setOpen(true); }}>
                  Nuova ricetta
                </IonButton>
              </div>
              {canManageFoodCost && (
                <IonSegment
                  value={recipeView}
                  onIonChange={(e) => setRecipeView((e.detail.value as 'operative' | 'foodcost') ?? 'operative')}
                  style={{ marginBottom: 8 }}
                >
                  <IonSegmentButton value="operative">
                    <IonLabel>Ricette ({operativeRecipes.length})</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="foodcost">
                    <IonLabel>Ricette FoodCost ({foodCostRecipes.length})</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
              )}
              <IonSearchbar
                value={recipeQuery}
                debounce={80}
                placeholder="Cerca ricetta…"
                onIonInput={(e) => setRecipeQuery(String(e.detail.value ?? ''))}
                style={{
                  '--background': '#fff',
                  '--border-radius': '12px',
                  '--box-shadow': '0 1px 3px rgba(15,23,42,.08)',
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: 4,
                } as React.CSSProperties}
              />
              {favoriteOwnerLabel && (
                <div style={{ margin: '2px 4px 8px', fontSize: 12, color: '#64748b' }}>
                  Le stelle sono i preferiti personali di {favoriteOwnerLabel}.
                </div>
              )}
              {filteredRecipes.length === 0 ? (
                <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
                  {recipeQuery.trim()
                    ? `Nessuna ricetta trovata per “${recipeQuery.trim()}”.`
                    : recipeView === 'foodcost'
                      ? 'Nessuna ricetta di solo food cost. Apri una ricetta e attiva “Solo Food Cost”, oppure creane una nuova qui.'
                      : 'Nessuna ricetta. Tocca + per crearne una.'}
                </div>
              ) : (
                filteredRecipes.map((r) => {
              const incomplete = !hasRecipeComposition(r);
              const isFavorite = favoriteIds.has(r.id);
              const accent = incomplete ? '#b45309' : isFavorite ? '#f59e0b' : '#0066cc';
              return (
                <IonItemSliding key={r.id} style={{ display: 'block', margin: '10px 0', borderRadius: 14 }}>
                <IonItem
                  lines="none"
                  detail={false}
                  style={{
                    '--background': 'transparent',
                    '--padding-start': '0px',
                    '--inner-padding-end': '0px',
                    '--min-height': '0px',
                    '--border-radius': '14px',
                  } as React.CSSProperties}
                >
                <div
                  onClick={() => { void openEdit(r); }}
                  role="button"
                  style={{
                    width: '100%',
                    background: '#fff',
                    borderRadius: 14,
                    borderLeft: `4px solid ${accent}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                  }}
                >
                  <button
                    type="button"
                    aria-label={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                    onClick={(ev) => { ev.stopPropagation(); void toggleFavorite(r); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 6,
                      cursor: 'pointer',
                      flexShrink: 0,
                      lineHeight: 1,
                    }}
                  >
                    <IonIcon
                      icon={isFavorite ? star : starOutline}
                      style={{ fontSize: 24, color: isFavorite ? '#f59e0b' : '#94a3b8' }}
                    />
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{r.name}</span>
                      {incomplete && (
                        <span style={{
                          background: '#fef3c7', color: '#b45309', fontSize: 10, fontWeight: 700,
                          padding: '2px 8px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 3,
                        }}>
                          <IonIcon icon={alertCircleOutline} style={{ fontSize: 12 }} />
                          INCOMPLETA
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{r.yield_qty} {r.yield_uom}</span>
                      {r.ingredients?.length > 0 && <span>· {r.ingredients.length} ingredienti</span>}
                      {r.chilling_enabled && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          · <IonIcon icon={snowOutline} style={{ fontSize: 12 }} /> {r.chilling_sign === 'negative' ? 'Neg' : 'Pos'}
                          {r.chilling_minutes != null && ` ${Math.floor(r.chilling_minutes / 60)}h${(r.chilling_minutes % 60).toString().padStart(2, '0')}`}
                        </span>
                      )}
                      {r.parent_recipe_ids?.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          · <IonIcon icon={layersOutline} style={{ fontSize: 12 }} /> {r.parent_recipe_ids.length} base
                        </span>
                      )}
                    </div>
                  </div>
                  {/* T-459: apertura rapida della scheda tecnica (PDF) dalla riga,
                      senza aprire l'editor. */}
                  {r.data_sheet_file_id && (
                    <button
                      type="button"
                      aria-label={`Scheda tecnica di ${r.name}`}
                      onClick={(ev) => { ev.stopPropagation(); void openTechSheet(r.data_sheet_file_id!); }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 6,
                        cursor: 'pointer',
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      <IonIcon icon={documentTextOutline} style={{ fontSize: 20, color: '#0066cc' }} />
                    </button>
                  )}
                </div>
                </IonItem>
                <IonItemOptions side="start">
                  <IonItemOption
                    color="primary"
                    onClick={() => duplicateRecipe(r)}
                    aria-label={`Duplica ${r.name}`}
                  >
                    <IonIcon slot="top" icon={copyOutline} />
                    Duplica
                  </IonItemOption>
                </IonItemOptions>
                </IonItemSliding>
              );
            })
              )}
            </>
          )}
        </div>

        <IonModal
          data-testid="recipe-editor"
          isOpen={open}
          onDidDismiss={() => {
            setOpen(false);
            if (!busy) {
              void recipeWorkDraft.release().catch(() => undefined);
              reset();
            }
          }}
          onDidPresent={() => { if (!draft.editId) focusInput(recipeNameRef.current, { select: true }); }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {variantView !== 'base'
                  ? `Variante: ${collettivaDietLabel(variantView)}`
                  : (draft.editId ? 'Modifica ricetta' : 'Nuova ricetta')}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton disabled={busy} onClick={() => {
                  setOpen(false);
                  reset();
                }}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <nav
            className="te-step-nav"
            aria-label="Sezioni ricetta"
            style={{ '--te-step-count': 3 } as React.CSSProperties}
          >
            <button type="button" data-testid="recipe-step-base" className={`te-step-nav__item ${editorStep === 'base' ? 'is-active' : ''}`} onClick={() => setEditorStep('base')}>
              <span>1</span>Dati base
            </button>
            <button type="button" data-testid="recipe-step-ingredients" className={`te-step-nav__item ${editorStep === 'ingredients' ? 'is-active' : ''}`} onClick={() => setEditorStep('ingredients')}>
              <span>2</span>Ingredienti
            </button>
            <button type="button" data-testid="recipe-step-safety" className={`te-step-nav__item ${editorStep === 'safety' ? 'is-active' : ''}`} onClick={() => setEditorStep('safety')}>
              <span>3</span>Validità e HACCP
            </button>
          </nav>
          <IonContent className="ion-padding">
            <div className={`te-work-draft-status te-work-draft-status--${recipeWorkDraft.status}`} role="status" aria-live="polite">
              {recipeWorkDraft.status === 'locked'
                ? 'Solo lettura: un altro dispositivo sta modificando questa ricetta.'
                : recipeWorkDraft.status === 'offline'
                  ? 'Bozza salvata sul dispositivo. Torna online prima del salvataggio finale.'
                  : recipeWorkDraft.status === 'editable'
                    ? 'Bozza salvata automaticamente per questo ristorante.'
                    : 'Preparo la bozza condivisa…'}
            </div>
            <div className={recipeDraftReadOnly ? 'te-work-draft-readonly' : undefined} aria-disabled={recipeDraftReadOnly}>
            {editorStep === 'base' && (
            <div className="te-form-step">
            {/* === ANAGRAFICA === */}
            <IonList inset>
              <IonListHeader><IonLabel>Anagrafica</IonLabel></IonListHeader>
              <IonItem className={fieldClass(draft.name.trim().length > 0)}>
                <IonInput
                  ref={recipeNameRef}
                  label="Nome ricetta *"
                  labelPlacement="stacked"
                  value={draft.name}
                  maxlength={200}
                  counter
                  counterFormatter={(inputLength, maxLength) => `${inputLength}/${maxLength}`}
                  placeholder="Es. Spaghetti pomodoro DOP"
                  onIonInput={(ev) => setDraft((d) => ({ ...d, name: String(ev.detail.value ?? '').slice(0, 200) }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  label="Descrizione (opzionale)"
                  labelPlacement="stacked"
                  value={draft.description}
                  onIonInput={(ev) => setDraft((d) => ({ ...d, description: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              {/* T-337: solo ristoratore/consulente/owner. Marca la ricetta come
                  "di solo food cost": esce dai picker di stampa/preparazione e
                  finisce nel segment "Ricette FoodCost". */}
              {canManageFoodCost && (
                <IonItem>
                  <IonToggle
                    checked={draft.isFoodCostOnly}
                    onIonChange={(ev) => setDraft((d) => ({ ...d, isFoodCostOnly: ev.detail.checked }))}
                  >
                    <IonLabel>
                      Solo Food Cost (non stampabile)
                      <IonNote style={{ display: 'block', fontSize: 12, whiteSpace: 'normal' }}>
                        Nascosta dalla stampa etichette e dalla preparazione. Serve
                        solo a calcolare il costo del piatto.
                      </IonNote>
                    </IonLabel>
                  </IonToggle>
                </IonItem>
              )}
              {/* T-450 F1: aliquota IVA del piatto (override). Vuoto = usa il
                  default del locale (Impostazioni). Serve al food cost per
                  scorporare l'IVA dal prezzo di vendita (lordo a menù). */}
              {canManageFoodCost && (
                <IonItem>
                  <IonInput
                    label="IVA del piatto (%)"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="decimal"
                    placeholder="usa il default del locale"
                    value={draft.vatRate}
                    onIonInput={(ev) => setDraft((d) => ({ ...d, vatRate: String(ev.detail.value ?? '') }))}
                  />
                  <IonNote slot="helper">
                    Facoltativo. Lascia vuoto per usare l'IVA di default del locale.
                    Es. 10% per la somministrazione; verifica vendita/asporto e prestazioni
                    separate con il commercialista.
                  </IonNote>
                </IonItem>
              )}
              <IonItem>
                <IonLabel position="stacked">
                  Quantità prodotta da una preparazione *
                </IonLabel>
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    placeholder="es. 4"
                    value={draft.yieldQty}
                    onIonInput={(ev) => setDraft((d) => ({ ...d, yieldQty: String(ev.detail.value ?? '') }))}
                    style={{ flex: 1 }}
                  />
                  <IonSelect
                    interface="popover"
                    value={draft.yieldUom}
                    onIonChange={(ev) => setDraft((d) => ({ ...d, yieldUom: String(ev.detail.value) }))}
                    style={{ flex: 1 }}
                  >
                    <IonSelectOption value="porzioni">porzioni</IonSelectOption>
                    <IonSelectOption value="kg">kg</IonSelectOption>
                    <IonSelectOption value="g">g</IonSelectOption>
                    <IonSelectOption value="l">l</IonSelectOption>
                    <IonSelectOption value="ml">ml</IonSelectOption>
                    <IonSelectOption value="pz">pezzi</IonSelectOption>
                  </IonSelect>
                </div>
                <IonNote slot="helper">
                  Una preparazione di questa ricetta produce N unità (es. "20 porzioni", "2 kg").
                  Serve per scaricare gli ingredienti in proporzione quando si producono mezze
                  ricette.
                  {collettivaEnabled && (
                    <> Se l'unità è <b>porzioni</b>, questo è anche il numero di porzioni usato
                      dal modulo collettiva (scaling e 1 etichetta per porzione) — non va inserito due volte.</>
                  )}
                </IonNote>
              </IonItem>
              {/* T-242: campo "Porzioni prodotte" mostrato SOLO se l'unità della
                  quantità prodotta NON è "porzioni". Quando è "porzioni" il numero
                  di porzioni = yield_qty (campo sopra) e si deriva in automatico:
                  evita la doppia domanda. Per ricette in kg/l/... serve invece
                  sapere quante porzioni produce il batch (per il flusso collettiva). */}
              {collettivaEnabled && draft.yieldUom.trim() !== 'porzioni' && (
                <IonItem>
                  <IonInput
                    label="Porzioni prodotte dalla ricetta"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="numeric"
                    placeholder="es. 50"
                    value={draft.yieldPortions}
                    onIonInput={(ev) => setDraft((d) => ({ ...d, yieldPortions: String(ev.detail.value ?? '') }))}
                  />
                  <IonNote slot="helper">
                    Quante porzioni produce un batch (es. 5 kg di ragù = 50 porzioni). Serve al
                    modulo collettiva per lo scaling e per stampare 1 etichetta per porzione.
                    Facoltativo.
                  </IonNote>
                </IonItem>
              )}
              {/* T-229: voce di menù di appartenenza (modulo collettiva) */}
              {collettivaEnabled && (
                <IonItem>
                  <IonSelect
                    label="Menù di appartenenza"
                    labelPlacement="stacked"
                    interface="popover"
                    value={draft.menuLabel}
                    onIonChange={(ev) => setDraft((d) => ({ ...d, menuLabel: String(ev.detail.value ?? '') }))}
                  >
                    <IonSelectOption value="">Nessuno</IonSelectOption>
                    <IonSelectOption value="A">Menù A</IonSelectOption>
                    <IonSelectOption value="B">Menù B</IonSelectOption>
                    {draft.menuLabel !== '' && draft.menuLabel !== 'A' && draft.menuLabel !== 'B' && (
                      <IonSelectOption value={draft.menuLabel}>{draft.menuLabel}</IonSelectOption>
                    )}
                  </IonSelect>
                  <IonNote slot="helper">
                    Usato dalla scheda di produzione (collettiva).
                  </IonNote>
                </IonItem>
              )}
            </IonList>

            {/* === SCHEDA TECNICA (T-459) === */}
            {/* Primo (e unico) allegato della ricetta: PDF/foto composti in un
                unico PDF, caricato solo al Salva. Il componente ha già il suo
                titolo + card, quindi non lo avvolgo in una IonList inset. */}
            <div style={{ padding: '0 4px' }}>
              <TechSheetPicker
                state={draft.techSheet}
                onChange={(ts) => setDraft((d) => ({ ...d, techSheet: ts }))}
              />
            </div>
            </div>
            )}

            {editorStep === 'ingredients' && (
            <div className="te-form-step">
            {/* === RICETTE BASE (nesting) — spostata sopra gli ingredienti (T-158) === */}
            {visibleRecipes.length > 0 && (
              <IonList inset>
                <IonListHeader><IonLabel>Ricette base (semi-lavorati)</IonLabel></IonListHeader>
                <IonItem>
                  <PickerTriggerField
                    label="Ricette già preparate da usare come ingrediente"
                    placeholder="Tocca per selezionare le ricette base"
                    value={draft.parentRecipes.length > 0
                      ? `${draft.parentRecipes.length} semilavorat${draft.parentRecipes.length === 1 ? 'o' : 'i'}`
                      : undefined}
                    onClick={() => setParentRecipePicker(true)}
                  />
                </IonItem>
                {/* T-259: semilavorati selezionati come righe-ingrediente con
                    quantità (in proporzione alla resa, come gli altri ingredienti). */}
                {draft.parentRecipes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 12px 4px' }}>
                    {draft.parentRecipes.map((sr, idx) => {
                      const sub = recipes.find((x) => x.id === sr.recipe_id);
                      return (
                        <div
                          key={sr.recipe_id}
                          style={{
                            background: '#fff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 12,
                            boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 1px 1px rgba(15,23,42,.04)',
                            padding: '8px 12px 12px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
                              background: '#ede9fe', color: '#6d28d9',
                              fontSize: 11, fontWeight: 700, letterSpacing: '.03em',
                            }}>
                              <IonIcon icon={layersOutline} style={{ fontSize: 12 }} />
                              Semilavorato {idx + 1}
                            </span>
                            <IonButton
                              fill="clear" color="danger" size="small"
                              style={{ margin: 0, height: 30, '--padding-start': '6px', '--padding-end': '6px' } as React.CSSProperties}
                              onClick={() => removeSubRecipe(sr.recipe_id)}
                              aria-label={`Rimuovi semilavorato ${sub?.name ?? ''}`}
                            >
                              <IonIcon icon={removeCircleOutline} slot="icon-only" />
                            </IonButton>
                          </div>

                          <div style={{
                            width: '100%',
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                            padding: '10px 12px', marginBottom: 10,
                          }}>
                            <span style={{
                              fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: '#0f172a',
                              whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere',
                            }}>
                              {sub?.name ?? '(ricetta non trovata)'}
                            </span>
                          </div>

                          {/* T-130: campo quantità solo se il tenant gestisce le dosi. */}
                          {recipeQtyEnabled && (
                            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                              <IonInput
                                label="Quantità usata"
                                labelPlacement="stacked"
                                fill="outline"
                                type="number"
                                inputmode="decimal"
                                value={sr.qty}
                                onIonInput={(ev) => patchSubRecipeQty(sr.recipe_id, String(ev.detail.value ?? ''))}
                                style={{ flex: 1 }}
                              />
                              <div style={{
                                flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px',
                                background: '#f8fafc',
                              }}>
                                <span style={{ fontSize: 11, color: '#64748b' }}>Unità (resa semilavorato)</span>
                                <span style={{ fontSize: 15, fontWeight: 600, color: (sr.uom || sub?.yield_uom) ? '#0f172a' : '#94a3b8' }}>
                                  {sr.uom || sub?.yield_uom || '—'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </IonList>
            )}

            {/* === T-441: VISTA (base / varianti dieta) ===
                Disponibile solo su una ricetta BASE già salvata (baseEditId).
                Le varianti sono ricette figlie salvate una alla volta: la tendina
                naviga tra base e varianti; ogni vista edita la propria ricetta. */}
            {baseEditId != null && (
              <IonList inset>
                <IonListHeader><IonLabel>Vista</IonLabel></IonListHeader>
                <IonItem lines="none">
                  <IonLabel position="stacked">Ingredienti di</IonLabel>
                  <IonSelect
                    value={variantView}
                    interface="popover"
                    onIonChange={(e) => { void onSwitchVariantView(String(e.detail.value)); }}
                  >
                    <IonSelectOption value="base">Base (ordinaria)</IonSelectOption>
                    {currentVariants.map((v) => (
                      <IonSelectOption key={v.id} value={v.diet_code ?? ''}>
                        {collettivaDietLabel(v.diet_code)} {staleVariants.has(v.diet_code ?? '') ? '⚠' : '✓'}
                      </IonSelectOption>
                    ))}
                    <IonSelectOption value="__add__">+ Aggiungi variante…</IonSelectOption>
                  </IonSelect>
                  <IonNote slot="helper">
                    {variantView === 'base'
                      ? 'Stai modificando la ricetta base. Aggiungi varianti dieta (diabetica, iposodica…) qui sopra.'
                      : 'Stai modificando una VARIANTE (ricetta a sé). Si salva separatamente dalla base.'}
                  </IonNote>
                </IonItem>
                {/* T-441 (deriva): la base è cambiata dopo questa variante →
                    invita a rivedere ingredienti/allergeni. Non bloccante. */}
                {variantView !== 'base' && staleVariants.has(variantView) && (
                  <IonItem lines="none">
                    <IonLabel className="ion-text-wrap" color="warning">
                      <p style={{ fontSize: 13, fontWeight: 600 }}>
                        ⚠ La ricetta base è cambiata dopo questa variante — rivedi ingredienti e allergeni.
                      </p>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      fill="outline"
                      onClick={() => {
                        // Ricopia gli ingredienti della base nel draft (l'utente
                        // riapplica poi le sue modifiche); il fingerprint si
                        // riallinea al salvataggio (variantFields in save()).
                        const baseIngs = (baseRecipeRow?.ingredients ?? []).map((ing) => ({
                          food_id: ing.food_id,
                          qty: ing.qty == null ? '' : String(ing.qty),
                          uom: ing.uom ?? '',
                        }));
                        setDraft((d) => ({ ...d, ingredients: baseIngs }));
                      }}
                    >
                      Allinea alla base
                    </IonButton>
                  </IonItem>
                )}
                {/* T-441: elimina (archivia) la variante corrente. */}
                {variantView !== 'base' && (
                  <IonItem lines="none">
                    <IonButton
                      fill="clear"
                      color="danger"
                      size="small"
                      disabled={busy}
                      onClick={async () => {
                        if (await confirmDialog('Eliminare questa variante dieta? La base e le altre varianti restano.', { okText: 'Elimina', danger: true })) {
                          void removeVariant();
                        }
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="start" />
                      Elimina variante
                    </IonButton>
                  </IonItem>
                )}
              </IonList>
            )}

            {/* === INGREDIENTI === */}
            <IonList inset>
              <IonListHeader>
                <IonLabel>Ingredienti ({draft.ingredients.length})</IonLabel>
              </IonListHeader>
              <IonItem button detail={false} onClick={addIngredient} color="primary" disabled={availableFoods.length === 0 && !canCreateFoods}>
                <IonIcon icon={addCircleOutline} slot="start" />
                <IonLabel>
                  {availableFoods.length === 0
                    ? (canCreateFoods ? 'Crea il primo alimento' : 'Nessun alimento disponibile')
                    : 'Aggiungi ingrediente'}
                </IonLabel>
              </IonItem>
              {canCreateFoods && availableFoods.length > 0 && (
                <IonItem button detail={false} onClick={openInlineFoodCreate}>
                  <IonIcon icon={addCircleOutline} slot="start" color="medium" />
                  <IonLabel color="medium">+ Crea nuovo alimento (non in lista)</IonLabel>
                </IonItem>
              )}
              {draft.ingredients.length === 0 ? (
                <IonItem lines="none">
                  <IonLabel color="medium" className="ion-text-wrap" style={{ fontSize: 13 }}>
                    Nessun ingrediente ancora. Tocca "Aggiungi ingrediente" sopra per pescare alimenti dal magazzino.
                  </IonLabel>
                </IonItem>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 12px 4px' }}>
                  {draft.ingredients.map((ing, idx) => {
                    const foodName = foods.find((f) => f.id === ing.food_id)?.name;
                    return (
                      <div
                        key={idx}
                        style={{
                          background: '#fff',
                          border: '1px solid #cbd5e1',
                          borderRadius: 12,
                          boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 1px 1px rgba(15,23,42,.04)',
                          padding: '8px 12px 12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                            background: '#dbeafe', color: '#1d4ed8',
                            fontSize: 11, fontWeight: 700, letterSpacing: '.03em',
                          }}>
                            Ingrediente {idx + 1}
                          </span>
                          <IonButton
                            fill="clear" color="danger" size="small"
                            style={{ margin: 0, height: 30, '--padding-start': '6px', '--padding-end': '6px' } as React.CSSProperties}
                            onClick={() => removeIngredient(idx)}
                            aria-label={`Rimuovi ingrediente ${idx + 1}`}
                          >
                            <IonIcon icon={removeCircleOutline} slot="icon-only" />
                          </IonButton>
                        </div>

                        <button
                          type="button"
                          onClick={() => setFoodSearchIdx(idx)}
                          aria-label={foodName ? `Alimento: ${foodName}. Tocca per cambiare` : 'Scegli un alimento'}
                          style={{
                            width: '100%', textAlign: 'left', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                            padding: '10px 12px', marginBottom: 10,
                          }}
                        >
                          <span style={{
                            fontSize: 15, fontWeight: 600, lineHeight: 1.3,
                            color: foodName ? '#0f172a' : '#94a3b8',
                            whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere',
                          }}>
                            {foodName || "Tocca per scegliere l'alimento"}
                          </span>
                          <IonIcon icon={chevronForward} style={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
                        </button>

                        {/* T-130: quantità/unità solo se il tenant gestisce le dosi.
                            Se OFF, la riga ingrediente è solo il nome. */}
                        {recipeQtyEnabled && (
                          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                            <IonInput
                              label="Quantità"
                              labelPlacement="stacked"
                              fill="outline"
                              type="number"
                              inputmode="decimal"
                              value={ing.qty}
                              onIonInput={(ev) => patchIngredient(idx, { qty: String(ev.detail.value ?? '') })}
                              style={{ flex: 1 }}
                            />
                            {/* T-221: l'unità NON è più una casella di testo libera —
                                è l'unità di carico merce dell'alimento (default_uom),
                                la stessa con cui il magazzino traccia la giacenza.
                                Così il moltiplicatore ricetta e lo scarico FEFO
                                lavorano sulla stessa unità, senza ambiguità g/kg. */}
                            <div style={{
                              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                              border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px',
                              background: '#f8fafc',
                            }}>
                              <span style={{ fontSize: 11, color: '#64748b' }}>Unità (da carico merce)</span>
                              <span style={{ fontSize: 15, fontWeight: 600, color: ing.uom ? '#0f172a' : '#94a3b8' }}>
                                {ing.uom || '—'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {computedAllergens.length > 0 && (
                <IonItem lines="none">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ fontSize: 12, color: '#666' }}>Allergeni rilevati automaticamente:</p>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                      {computedAllergens.map((a) => (
                        <IonChip key={a} color="warning">{a}</IonChip>
                      ))}
                    </div>
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
            </div>
            )}

            {editorStep === 'safety' && (
            <div className="te-form-step">
            {/* === SHELF LIFE === */}
            <IonList inset>
              <IonListHeader><IonLabel>Validità preparato</IonLabel></IonListHeader>
              <IonItem>
                <IonLabel position="stacked">Shelf-life (per etichette)</IonLabel>
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    placeholder="es. 3"
                    value={draft.shelfLifeQty}
                    onIonInput={(ev) => setDraft((d) => ({ ...d, shelfLifeQty: String(ev.detail.value ?? '') }))}
                    style={{ flex: 1 }}
                  />
                  <IonSelect
                    interface="popover"
                    value={draft.shelfLifeUnit}
                    onIonChange={(ev) => setDraft((d) => ({ ...d, shelfLifeUnit: ev.detail.value as ShelfUnit }))}
                    style={{ flex: 1 }}
                  >
                    <IonSelectOption value="ore">ore</IonSelectOption>
                    <IonSelectOption value="giorni">giorni</IonSelectOption>
                    <IonSelectOption value="settimane">settimane</IonSelectOption>
                    <IonSelectOption value="mesi">mesi</IonSelectOption>
                  </IonSelect>
                </div>
                <IonNote slot="helper">
                  Stampando un'etichetta di questa ricetta, la scadenza viene calcolata da qui.
                  Vuoto = decisi al momento della stampa.
                </IonNote>
              </IonItem>
            </IonList>

            {/* === ABBATTIMENTO === */}
            <IonList inset>
              <IonListHeader><IonLabel>Trattamento HACCP</IonLabel></IonListHeader>
              {/* T-422: tipo di controllo temperatura al cuore per la Scheda di
                  produzione. 'freddo' rende la temperatura facoltativa (niente
                  falso allarme <74 °C per insalate ecc.); 'cottura' resta 74 °C. */}
              <IonItem>
                <IonLabel position="stacked">Controllo temperatura al cuore</IonLabel>
                <IonSelect
                  interface="action-sheet"
                  value={draft.ccpType}
                  onIonChange={(ev) => setDraft((d) => ({ ...d, ccpType: (ev.detail.value as CcpType) ?? 'cottura' }))}
                >
                  <IonSelectOption value="cottura">{CCP_TYPE_LABELS.cottura}</IonSelectOption>
                  <IonSelectOption value="freddo">{CCP_TYPE_LABELS.freddo}</IonSelectOption>
                </IonSelect>
                <IonNote slot="helper">
                  “Servito freddo” (insalate, frutta cruda…): in Scheda di produzione la
                  temperatura al cuore è facoltativa e non segnala errore.
                </IonNote>
              </IonItem>
              <IonItem>
                <IonToggle
                  checked={draft.chillingEnabled}
                  onIonChange={(ev) => setDraft((d) => ({ ...d, chillingEnabled: ev.detail.checked }))}
                >
                  <IonLabel>Prevede abbattimento</IonLabel>
                </IonToggle>
              </IonItem>
              {draft.chillingEnabled && (
                <>
                  <IonItem>
                    <IonLabel position="stacked">Tempo abbattimento</IonLabel>
                    <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                      <IonSelect
                        interface="popover"
                        value={draft.chillingHours}
                        onIonChange={(ev) => setDraft((d) => ({ ...d, chillingHours: String(ev.detail.value) }))}
                        style={{ flex: 1 }}
                      >
                        {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((h) => (
                          <IonSelectOption key={h} value={h}>{h} h</IonSelectOption>
                        ))}
                      </IonSelect>
                      <IonSelect
                        interface="popover"
                        value={draft.chillingMinutes}
                        onIonChange={(ev) => setDraft((d) => ({ ...d, chillingMinutes: String(ev.detail.value) }))}
                        style={{ flex: 1 }}
                      >
                        {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((m) => (
                          <IonSelectOption key={m} value={m}>{m} m</IonSelectOption>
                        ))}
                      </IonSelect>
                    </div>
                  </IonItem>
                  {/* T-154: selettore "Tipo abbattimento" rimosso (richiesta utente:
                      richiamava la card Impostazioni eliminata, confondeva). Il campo
                      draft.chillingSign resta col suo default per non rompere il
                      constraint recipes_chilling_consistency. */}
                  <IonItem>
                    <IonInput
                      label="Temp. iniziale (°C)"
                      labelPlacement="stacked"
                      type="number"
                      inputmode="decimal"
                      value={draft.tempStart}
                      onIonInput={(ev) => setDraft((d) => ({ ...d, tempStart: String(ev.detail.value ?? '') }))}
                    />
                    <IonInput
                      label="Temp. finale (°C)"
                      labelPlacement="stacked"
                      type="number"
                      inputmode="decimal"
                      value={draft.tempEnd}
                      onIonInput={(ev) => setDraft((d) => ({ ...d, tempEnd: String(ev.detail.value ?? '') }))}
                    />
                  </IonItem>
                </>
              )}
              <IonItem>
                <IonToggle
                  checked={draft.freezingEnabled}
                  onIonChange={(ev) => setDraft((d) => ({ ...d, freezingEnabled: ev.detail.checked }))}
                >
                  <IonLabel>Prevede congelamento</IonLabel>
                </IonToggle>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">
                  {draft.freezingEnabled
                    ? 'Shelf life per scongelamento'
                    : 'Shelf life per apertura confezione'}
                </IonLabel>
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    min="0"
                    placeholder={draft.freezingEnabled ? 'es. 48' : 'es. 3'}
                    value={draft.freezingEnabled
                      ? draft.thawedShelfLifeQty
                      : draft.openedShelfLifeQty}
                    onIonInput={(ev) => {
                      const value = String(ev.detail.value ?? '');
                      setDraft((d) => d.freezingEnabled
                        ? { ...d, thawedShelfLifeQty: value }
                        : { ...d, openedShelfLifeQty: value });
                    }}
                    style={{ flex: 1 }}
                  />
                  <IonSelect
                    interface="popover"
                    value={draft.freezingEnabled
                      ? draft.thawedShelfLifeUnit
                      : draft.openedShelfLifeUnit}
                    onIonChange={(ev) => {
                      const unit = ev.detail.value as ShelfUnit;
                      setDraft((d) => d.freezingEnabled
                        ? { ...d, thawedShelfLifeUnit: unit }
                        : { ...d, openedShelfLifeUnit: unit });
                    }}
                    style={{ flex: 1 }}
                  >
                    <IonSelectOption value="ore">ore</IonSelectOption>
                    <IonSelectOption value="giorni">giorni</IonSelectOption>
                    <IonSelectOption value="settimane">settimane</IonSelectOption>
                    <IonSelectOption value="mesi">mesi</IonSelectOption>
                  </IonSelect>
                </div>
                <IonNote slot="helper">
                  {draft.freezingEnabled
                    ? 'Usata automaticamente quando questa preparazione viene scongelata.'
                    : 'Distinta dalla shelf life del preparato: si applica solo dopo l’apertura.'}
                </IonNote>
              </IonItem>
            </IonList>
            </div>
            )}

            <div style={{ height: 80 }} />
            </div>
          </IonContent>
          {/* Sticky save bar */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--ion-background-color, #fff)',
            borderTop: '1px solid var(--ion-color-step-150, #ddd)',
            padding: '12px 16px',
            display: 'flex',
            gap: 8,
          }}>
            {/* Archivio della BASE. In vista variante l'archiviazione della singola
                variante è nel pannello Vista ("Elimina variante"); qui si archivia
                la ricetta base (e a cascata le sue varianti — T-441). */}
            {draft.editId && variantView === 'base' && (
              <IonButton
                fill="outline"
                color="danger"
                disabled={busy || !recipeDraftFinalizable}
                onClick={async () => {
                  const msg = currentVariants.length > 0
                    ? `Archiviare questa ricetta e le sue ${currentVariants.length} variant${currentVariants.length === 1 ? 'e' : 'i'} dieta? Non saranno più visibili nella lista, ma le etichette già stampate restano valide.`
                    : 'Archiviare questa ricetta? Non sarà più visibile nella lista, ma le etichette già stampate restano valide.';
                  if (await confirmDialog(msg, { okText: 'Archivia', danger: true })) {
                    void archiveRecipe();
                  }
                }}
                aria-label="Archivia ricetta"
              >
                <IonIcon icon={trashOutline} slot="icon-only" />
              </IonButton>
            )}
            {editorStep !== 'base' && (
              <IonButton
                fill="outline"
                expand="block"
                onClick={() => setEditorStep(editorStep === 'safety' ? 'ingredients' : 'base')}
                style={{ flex: 1 }}
              >
                Indietro
              </IonButton>
            )}
            {editorStep !== 'safety' ? (
              <IonButton
                expand="block"
                color="primary"
                onClick={() => setEditorStep(editorStep === 'base' ? 'ingredients' : 'safety')}
                style={{ flex: 2 }}
              >
                Continua
              </IonButton>
            ) : (
              <IonButton
                expand="block"
                color="primary"
                disabled={busy || !recipeDraftFinalizable}
                onClick={() => { void save(); }}
                style={{ flex: 2 }}
              >
                {busy
                  ? 'Salvataggio…'
                  : variantView !== 'base'
                    ? 'Salva variante'
                    : (draft.editId ? 'Salva modifiche' : 'Salva ricetta')}
              </IonButton>
            )}
          </div>
        </IonModal>

        <IonAlert
          isOpen={isActive && !!errorAlert}
          header="Errore salvataggio"
          message={errorAlert ?? ''}
          buttons={['OK']}
          onDidDismiss={() => setErrorAlert(null)}
        />

        {/* Alert non-bloccante: ricetta salvata ma senza ingredienti.
            L'utente è informato; può continuare a usare l'app normalmente. */}
        <IonAlert
          isOpen={isActive && !!incompleteAlert}
          header="Ricetta incompleta"
          message={incompleteAlert ?? ''}
          buttons={['OK']}
          onDidDismiss={() => setIncompleteAlert(null)}
        />

        {/* FoodEditorModal: aperto inline da "+ Crea nuovo alimento" o da
            empty-state. Quando salva, il food viene aggiunto agli ingredienti. */}
        <FoodEditorModal
          isOpen={foodEditorOpen && canCreateFoods}
          onDismiss={() => { setFoodEditorOpen(false); setFoodEditorAppend(false); }}
          onSaved={handleFoodCreated}
        />

        {/* Selettore ricerca alimento per la riga ingrediente. */}
        <SearchPickerModal
          isOpen={foodSearchIdx !== null}
          title="Scegli un alimento"
          wrapItems
          focusSearchOnOpen
          searchPlaceholder="Cerca alimento…"
          selectedId={foodSearchIdx !== null ? draft.ingredients[foodSearchIdx]?.food_id : undefined}
          emptyHint="Nessun alimento. Crea un alimento dalla scheda Magazzino."
          primaryAction={canCreateFoods ? {
            label: 'Crea nuovo alimento',
            onClick: () => { setFoodSearchIdx(null); openInlineFoodCreate(); },
          } : undefined}
          items={availableFoods
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((f) => ({ id: f.id, label: f.name }))}
          onSelect={(id) => {
            const f = availableFoods.find((x) => x.id === id);
            if (foodSearchIdx === ADD_INGREDIENT_IDX) {
              // Nuovo ingrediente: la riga nasce ora, con l'alimento scelto.
              setDraft((d) => ({
                ...d,
                ingredients: [...d.ingredients, { food_id: id, qty: '1', uom: f?.default_uom || 'pz' }],
              }));
            } else if (foodSearchIdx !== null) {
              patchIngredient(foodSearchIdx, f?.default_uom ? { food_id: id, uom: f.default_uom } : { food_id: id });
            }
            setFoodSearchIdx(null);
          }}
          onClose={() => setFoodSearchIdx(null)}
        />

        {/* Selettore ricerca multiplo: ricette base (semi-lavorati). */}
        <SearchPickerModal
          isOpen={parentRecipePicker}
          title="Ricette base"
          searchPlaceholder="Cerca ricetta…"
          multiple
          selectedIds={draft.parentRecipes.map((p) => p.recipe_id)}
          emptyHint="Nessuna ricetta disponibile."
          // T-259: i semilavorati GIÀ selezionati vanno in cima (così si capisce
          // a colpo d'occhio cosa è selezionato); il resto in ordine alfabetico.
          // Esclude la ricetta in modifica (non può essere base di sé stessa).
          items={(() => {
            const selSet = new Set(draft.parentRecipes.map((p) => p.recipe_id));
            return visibleRecipes
              .filter((r) => r.id !== draft.editId)
              .slice()
              .sort((a, b) => {
                const aSel = selSet.has(a.id) ? 0 : 1;
                const bSel = selSet.has(b.id) ? 0 : 1;
                if (aSel !== bSel) return aSel - bSel;
                return a.name.localeCompare(b.name);
              })
              .map((r) => ({ id: r.id, label: r.name, sublabel: selSet.has(r.id) ? 'Selezionato' : undefined }));
          })()}
          onConfirm={confirmSubRecipes}
          onClose={() => setParentRecipePicker(false)}
        />

        {/* T-441: modal "Aggiungi variante dieta". Elenca SOLO le diete
            variantabili non ancora definite per questa base. Tap → crea la
            ricetta figlia (copia della base) e la apre per modificarla. */}
        <IonModal isOpen={addVariantOpen} onDidDismiss={() => setAddVariantOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Aggiungi variante dieta</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setAddVariantOpen(false)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonList inset>
              <IonItem lines="none">
                <IonLabel className="ion-text-wrap" color="medium">
                  <p style={{ fontSize: 13 }}>
                    Una variante è una copia della ricetta base con i suoi
                    ingredienti (es. meno pasta per la diabetica, senza sale per
                    l'iposodica). Si aggancia automaticamente in Pianifica
                    produzione.
                  </p>
                </IonLabel>
              </IonItem>
              {(() => {
                const available = VARIANTABLE_DIET_CODES.filter(
                  (code) => !currentVariants.some((v) => v.diet_code === code),
                );
                if (available.length === 0) {
                  return (
                    <IonItem lines="none">
                      <IonLabel color="medium" className="ion-text-wrap" style={{ fontStyle: 'italic', fontSize: 13 }}>
                        Tutte le varianti disponibili sono già definite.
                      </IonLabel>
                    </IonItem>
                  );
                }
                return available.map((code) => (
                  <IonItem button key={code} detail disabled={busy} onClick={() => { void createVariant(code); }}>
                    <IonLabel>{collettivaDietLabel(code)}</IonLabel>
                  </IonItem>
                ));
              })()}
            </IonList>
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2200}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
